import { ApiSyncType } from "@incendium/api";
import { ArrowForward, CheckCircle } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import WooLogo from "Assets/Images/ThirdParty/woocommerce-logo-color-black@2x.png";
import WooCommerceConnectionStatus from "features/dataConnectors/components/WooCommerceConnectionStatus";
import { normalizeStoreUrl } from "features/dataConnectors/utils/normalizeStoreUrl";
import {
  fetchWooCommerceSync,
  triggerWooCommerceSync,
} from "features/dataConnectors/services/woocommerceSync";
import {
  getWooCommerceConnectionStatus,
  isWooCommercePluginDetected,
  openWooCommercePluginInstaller,
  resolveWooCommerceStoreUrl,
} from "features/dataConnectors/utils/woocommerceStoreMeta";
import OnboardingSuperAdminSkipButton from "features/onboarding/components/OnboardingSuperAdminSkipButton";
import WooCommerceOnboardingSecurityNote from "features/onboarding/woocommerce/WooCommerceOnboardingSecurityNote";
import { useSelectedProject, usePolling } from "Hooks";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const StepNumber = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "50%",
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "0.875rem",
  flexShrink: 0,
}));

const INSTALL_STEPS = [
  {
    title: "Open your WordPress plugin installer",
    description:
      "We'll open the Incendium plugin page on your store so you can install it.",
  },
  {
    title: "Install Incendium",
    description: "Click Install Now on the Incendium plugin.",
  },
  {
    title: "Activate the plugin",
    description: "Activate it in WordPress, then return here.",
  },
] as const;

interface IEcomPlatformOnboardingStep2Props {
  onContinue: () => void;
}

function EcomPlatformOnboardingStep2({
  onContinue,
}: IEcomPlatformOnboardingStep2Props) {
  const { selectedProject } = useSelectedProject();
  const { externalSyncs, refetch: refetchExternalSyncs } = useExternalSyncs();
  const [syncTriggering, setSyncTriggering] = useState(false);
  const [checkingContinue, setCheckingContinue] = useState(false);
  const [continueCheckMessage, setContinueCheckMessage] = useState<
    string | null
  >(null);
  const syncTriggeredRef = useRef(false);

  const wooSync = useMemo(
    () =>
      externalSyncs.find((s) => s.type === ApiSyncType.WOOCOMMERCE_CONNECTOR),
    [externalSyncs],
  );

  const pluginDetected = isWooCommercePluginDetected(wooSync);
  const connectionStatus = wooSync
    ? getWooCommerceConnectionStatus(wooSync)
    : null;

  const storeUrl = useMemo(() => {
    const domain = (selectedProject?.domains || []).find(
      (d) => d.isPrimary && d.domain,
    )?.domain;
    return resolveWooCommerceStoreUrl(
      externalSyncs,
      domain ? normalizeStoreUrl(domain) : undefined,
    );
  }, [externalSyncs, selectedProject?.domains]);

  const refreshConnectionStatus = useCallback(async () => {
    if (!selectedProject?.id) {
      return;
    }
    await triggerWooCommerceSync(selectedProject.id as number);
    await refetchExternalSyncs(true);
  }, [selectedProject?.id, refetchExternalSyncs]);

  const runWooCommerceSync = useCallback(async () => {
    if (!selectedProject?.id) {
      return;
    }
    setSyncTriggering(true);
    try {
      await refreshConnectionStatus();
    } finally {
      setSyncTriggering(false);
    }
  }, [selectedProject?.id, refreshConnectionStatus]);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }
    refetchExternalSyncs(true);
    // Only re-fetch when the project changes; refetch is not referentially stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!selectedProject?.id || syncTriggeredRef.current) {
      return;
    }
    syncTriggeredRef.current = true;
    runWooCommerceSync();
  }, [selectedProject?.id, runWooCommerceSync]);

  const { loading: pollingSyncs } = usePolling({
    pollFn: refreshConnectionStatus,
    interval: 5000,
    enabled: !!selectedProject?.id && !pluginDetected,
    immediate: false,
  });

  const handleContinue = useCallback(async () => {
    if (pluginDetected) {
      onContinue();
      return;
    }
    if (!selectedProject?.id || !wooSync) {
      return;
    }

    setContinueCheckMessage(null);
    setCheckingContinue(true);
    try {
      await runWooCommerceSync();
      const latestWooSync = await fetchWooCommerceSync(
        selectedProject.id as number,
      );
      await refetchExternalSyncs(true);

      if (isWooCommercePluginDetected(latestWooSync)) {
        onContinue();
        return;
      }

      const status = latestWooSync
        ? getWooCommerceConnectionStatus(latestWooSync)
        : null;
      setContinueCheckMessage(
        status?.message ||
          "We couldn't reach the Incendium plugin yet. Install and activate it on WordPress, then try again.",
      );
    } catch {
      setContinueCheckMessage(
        "Failed to check your store. Please try again in a moment.",
      );
    } finally {
      setCheckingContinue(false);
    }
  }, [
    pluginDetected,
    onContinue,
    selectedProject?.id,
    wooSync,
    runWooCommerceSync,
    refetchExternalSyncs,
  ]);

  const openPluginInstaller = () => {
    if (!storeUrl) {
      return;
    }
    openWooCommercePluginInstaller(storeUrl);
  };

  const busy = syncTriggering || pollingSyncs || checkingContinue;

  return (
    <Stack
      spacing={3}
      justifyContent="space-between"
      sx={{ minHeight: "100%" }}
    >
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src={WooLogo}
            alt="WooCommerce"
            sx={{ height: 32, width: "auto" }}
          />
        </Stack>

        <Typography variant="subtitle1" fontWeight={600}>
          Install Incendium for WooCommerce
        </Typography>

        {pluginDetected && (
          <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
            <Typography variant="subtitle2">
              Incendium plugin detected on your store
            </Typography>
            <Typography variant="body2" mt={0.5}>
              We&apos;re finishing setup in the background. You can continue to
              the next step.
            </Typography>
          </Alert>
        )}

        {continueCheckMessage && !pluginDetected && (
          <Alert severity="warning">
            <Typography variant="body2">{continueCheckMessage}</Typography>
          </Alert>
        )}

        {wooSync && connectionStatus && !pluginDetected && (
          <WooCommerceConnectionStatus sync={wooSync} refreshing={busy} />
        )}

        {!pluginDetected && (
          <Stack spacing={2.5}>
            {INSTALL_STEPS.map((step, index) => (
              <Stack
                key={step.title}
                direction="row"
                spacing={2}
                alignItems="flex-start"
              >
                <StepNumber>{index + 1}</StepNumber>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}

        <WooCommerceOnboardingSecurityNote>
          If you&apos;re not logged in, WordPress may ask you to sign in first.
        </WooCommerceOnboardingSecurityNote>
      </Stack>

      <Stack spacing={2}>
        <Divider />
        {!pluginDetected && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowForward />}
            onClick={openPluginInstaller}
            disabled={!storeUrl}
          >
            Open Plugin Installer
          </Button>
        )}
        <Button
          fullWidth
          variant={pluginDetected ? "contained" : "text"}
          color="primary"
          size="large"
          endIcon={
            busy && !pluginDetected ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <ArrowForward />
            )
          }
          onClick={handleContinue}
          disabled={!wooSync || (busy && !pluginDetected)}
        >
          {pluginDetected
            ? "Continue"
            : checkingContinue
              ? "Checking installation…"
              : "Already installed? Continue"}
        </Button>
        <OnboardingSuperAdminSkipButton
          onSkip={onContinue}
          label="Skip — continue without plugin"
        />
      </Stack>
    </Stack>
  );
}

export default EcomPlatformOnboardingStep2;
